/**
 * Fase 8 — chamadas e queries do Assistente de IA: status/cota, uso do mês, conversas, streaming
 * (SSE lido com `fetch` + ReadableStream, com "parar geração" por AbortController), variações,
 * estrutura → rascunho e regras sugeridas.
 */
import type {
  AiConversationDetailDto,
  AiConversationDto,
  AiDraftFromStructureDto,
  AiRuleSuggestionsDto,
  AiStatusDto,
  AiStreamEvent,
  AiStructureDto,
  AiUsageDto,
  AiVariationsDto,
  CreateConversationInput,
  CreateDraftFromStructureInput,
  CreateSuggestedRuleInput,
  DiagnoseInput,
  GenerateVariationsInput,
  RuleDto,
  SendMessageInput,
  StructureBriefingInput,
} from "@iagenix/shared";
import { queryOptions } from "@tanstack/react-query";
import { api, ApiRequestError } from "./api";

const ws = (id: string) => `/api/workspaces/${id}/ai`;

export const aiKey = (id: string) => ["workspaces", id, "ai"];

export const aiQueries = {
  status: (id: string) =>
    queryOptions({ queryKey: [...aiKey(id), "status"], queryFn: () => api<AiStatusDto>(`${ws(id)}/status`) }),
  usage: (id: string, month?: string) =>
    queryOptions({
      queryKey: [...aiKey(id), "usage", month ?? "atual"],
      queryFn: () => api<AiUsageDto>(`${ws(id)}/usage${month ? `?month=${month}` : ""}`),
    }),
  conversations: (id: string) =>
    queryOptions({
      queryKey: [...aiKey(id), "conversations"],
      queryFn: () => api<AiConversationDto[]>(`${ws(id)}/conversations`),
    }),
  conversation: (id: string, conversationId: string) =>
    queryOptions({
      queryKey: [...aiKey(id), "conversation", conversationId],
      queryFn: () => api<AiConversationDetailDto>(`${ws(id)}/conversations/${conversationId}`),
    }),
};

export const aiApi = {
  createConversation: (id: string, body: CreateConversationInput) =>
    api<AiConversationDto>(`${ws(id)}/conversations`, { method: "POST", body }),
  deleteConversation: (id: string, conversationId: string) =>
    api<undefined>(`${ws(id)}/conversations/${conversationId}`, { method: "DELETE" }),
  variations: (id: string, body: GenerateVariationsInput) =>
    api<AiVariationsDto>(`${ws(id)}/variations`, { method: "POST", body }),
  structure: (id: string, body: StructureBriefingInput) =>
    api<AiStructureDto>(`${ws(id)}/structure`, { method: "POST", body }),
  structureDraft: (id: string, body: CreateDraftFromStructureInput) =>
    api<AiDraftFromStructureDto>(`${ws(id)}/structure/draft`, { method: "POST", body }),
  suggestRules: (id: string, clientId: string) =>
    api<AiRuleSuggestionsDto>(`${ws(id)}/rules/suggest`, { method: "POST", body: { clientId } }),
  createSuggestedRule: (id: string, body: CreateSuggestedRuleInput) =>
    api<RuleDto>(`${ws(id)}/rules/create`, { method: "POST", body }),
};

export interface StreamHandlers {
  onEvent: (event: AiStreamEvent) => void;
}

/**
 * POST que devolve `text/event-stream`. Erros ANTES do início (cota, validação, permissão) chegam como
 * JSON e viram `ApiRequestError`; depois do início viram evento `error`.
 */
export async function streamAi(
  path: string,
  body: unknown,
  handlers: StreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      credentials: "same-origin",
      signal,
    });
  } catch (err) {
    if (signal.aborted) return;
    throw new ApiRequestError(0, "sem_conexao", "Sem conexão com o servidor. Verifique sua internet.", err);
  }
  if (!res.ok) {
    const text = await res.text();
    const parse = (): { error?: { code?: string; message?: string; details?: unknown } } | null => {
      try {
        return JSON.parse(text) as { error?: { code?: string; message?: string; details?: unknown } };
      } catch {
        return null;
      }
    };
    const error = parse()?.error;
    throw new ApiRequestError(res.status, error?.code ?? "erro_http", error?.message ?? `O servidor respondeu com erro ${res.status}.`, error?.details);
  }
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx = buffer.indexOf("\n\n");
      while (idx >= 0) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const data = block
          .split("\n")
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).trim())
          .join("\n");
        if (data) {
          try {
            handlers.onEvent(JSON.parse(data) as AiStreamEvent);
          } catch {
            // ignora linhas que não são JSON (keep-alive)
          }
        }
        idx = buffer.indexOf("\n\n");
      }
    }
  } catch (err) {
    if (!signal.aborted) throw err;
  } finally {
    reader.releaseLock();
  }
}

export function streamMessage(
  workspaceId: string,
  conversationId: string,
  body: SendMessageInput,
  handlers: StreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  return streamAi(`${ws(workspaceId)}/conversations/${conversationId}/messages`, body, handlers, signal);
}

export function streamDiagnosis(
  workspaceId: string,
  body: DiagnoseInput,
  handlers: StreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  return streamAi(`${ws(workspaceId)}/diagnose`, body, handlers, signal);
}

/** Dólares a partir de micro-dólares, em pt-BR (US$). */
export function formatUsdMicros(micros: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "USD", maximumFractionDigits: micros < 10_000 ? 4 : 2 }).format(micros / 1_000_000);
}
